import React, { useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, X } from "lucide-react";
import { RemainingCreditsContext } from "../providers/RemainingCreditsProvider";

const APPSUMO_REVIEW_URL = "https://appsumo.com/products/writevoice";
const DISMISS_KEY = "appsumo_review_banner_dismissed_v1";

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* no-op */
  }
};

export const AppSumoReviewBanner: React.FC = () => {
  const { limits } = useContext(RemainingCreditsContext);
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  // Only show when the user has an AppSumo license attached
  // (regardless of whether the license is currently active).
  const shouldRender = !!limits?.isAppSumo && !dismissed;

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  const handleClaim = () => {
    window.open(APPSUMO_REVIEW_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence initial={false}>
      {shouldRender && (
        <motion.div
          key="appsumo-banner"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative w-full overflow-hidden"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={handleClaim}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClaim();
              }
            }}
            className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 text-white cursor-pointer hover:brightness-105 transition-all"
            style={{
              background:
                "linear-gradient(90deg, #c2410c 0%, #ea580c 35%, #f97316 70%, #fb923c 100%)",
            }}
          >
            {/* Icon */}
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm hidden sm:flex items-center justify-center">
              <Gift className="w-5 h-5" strokeWidth={2} />
            </div>

            {/* Message */}
            <p className="flex-1 text-sm sm:text-[15px] leading-snug min-w-0">
              <span className="font-semibold">
                FREE bonus: 100,000 words added to your account.
              </span>{" "}
              <span className="opacity-90 underline decoration-white/40 underline-offset-2 hover:decoration-white/80">
                Drop us a review on AppSumo — it takes less than 1 min.
              </span>
            </p>

            {/* CTA */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={(e) => {
                e.stopPropagation();
                handleClaim();
              }}
              className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-orange-600 hover:text-orange-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
            >
              <span>Claim my words</span>
              <span aria-hidden="true">🎁</span>
            </motion.button>

            {/* Close */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              aria-label="Dismiss"
              className="flex-shrink-0 p-1.5 rounded-full hover:bg-white/15 transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppSumoReviewBanner;
